import { useState, useCallback, useRef, useEffect } from "react";
import { ScrollView } from "react-native";
import { View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { WebLayout, useTenantSwitcherControl } from "./WebLayout.web";
import { SettingsSection } from "./SettingsSection.web";
import { SettingsListItem } from "./SettingsListItem.web";
import { useRouter } from "expo-router";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { useTenant, getTenantDisplayName, useAuth } from "@src/features/tenants";
import { FEATURE_FLAGS, getBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { ImpersonationSetting } from "../settings/ImpersonationSetting";

/**
 * Settings page with web layout.
 * Organisation switcher is managed globally by WebLayout.
 */
export function SettingsWebLayout() {
  const router = useRouter();
  const { selectedTenant } = useTenant();
  const tenantSwitcher = useTenantSwitcherControl();
  const { contactUrl, termsUrl, privacyUrl, supportPhone, supportPhoneDisplay } = useRegionConfig();
  const { user } = useAuth();

  const isSupportUser = user?.roles?.includes("a3s:support") ?? false;

  const isUsersAndPermissionsOptionEnabled = getBooleanFlag(FEATURE_FLAGS.USERS_PERMISSIONS_OPTION);

  const handleOpenURL = useCallback(async (url: string) => {
    window.open(url, "_blank");
  }, []);

  const [copiedPhone, setCopiedPhone] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const handleCopyPhone = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(supportPhone);
      setCopiedPhone(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedPhone(false), 2000);
    } catch {
      // Fallback: the phone number is displayed as subtitle, user can copy manually
    }
  }, [supportPhone]);

  const settingsContent = (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 80,
        maxWidth: sizes.webContentMaxWidth,
      }}
      showsVerticalScrollIndicator={false}
    >
      <View gap="6xl">
        {/* General Section */}
        <SettingsSection title="General">
          {tenantSwitcher && (
            <SettingsListItem
              title="Organisation"
              subtitle={selectedTenant ? getTenantDisplayName(selectedTenant) : "Not selected"}
              onPress={() => tenantSwitcher.open()}
            />
          )}
          {isUsersAndPermissionsOptionEnabled && (
            <SettingsListItem
              title="Users & Permissions"
              onPress={() => router.push("/settings/users-and-permissions")}
            />
          )}
        </SettingsSection>

        {/* Contact Section */}
        <SettingsSection title="Contact">
          <SettingsListItem
            title={copiedPhone ? "Copied!" : "Support"}
            subtitle={supportPhoneDisplay}
            onPress={handleCopyPhone}
            showChevron={false}
          />
          <SettingsListItem title="Get a quote" onPress={() => handleOpenURL(contactUrl)} />
          <SettingsListItem title="Contact us" onPress={() => handleOpenURL(contactUrl)} />
        </SettingsSection>

        {/* Legal Section */}
        <SettingsSection title="Legal">
          <SettingsListItem title="Terms & Conditions" onPress={() => handleOpenURL(termsUrl)} />
          <SettingsListItem title="Privacy" onPress={() => handleOpenURL(privacyUrl)} />
        </SettingsSection>

        {/* Support Section — only visible to a3s:support users */}
        {isSupportUser && (
          <SettingsSection title="Support Tools">
            <ImpersonationSetting />
          </SettingsSection>
        )}
      </View>
    </ScrollView>
  );

  return <WebLayout>{settingsContent}</WebLayout>;
}
